if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      // HAPPY FARMS: Sprachbilder-Feature.
      // Map aus product-media-gallery.liquid: Standard-Media-ID -> Sprachbild-Media-ID.
      // Ohne Sprach-Set fehlt das Attribut, dann ist die gesamte Logik hier inert.
      get variantMediaMap() {
        if (this._variantMediaMap === undefined) {
          try {
            this._variantMediaMap = this.dataset.variantMediaMap ? JSON.parse(this.dataset.variantMediaMap) : null;
          } catch (error) {
            console.warn('media-gallery: data-variant-media-map ist kein gueltiges JSON', error);
            this._variantMediaMap = null;
          }
        }
        return this._variantMediaMap;
      }

      // Neutrales Sprachbild ohne Groessenaufdruck (siehe product-media-gallery.liquid).
      get variantMediaFallback() {
        return this.dataset.variantMediaFallback || null;
      }

      // Rohe Media-ID des Varianten-Bilds -> rohe Media-ID des passenden Sprachbilds.
      // Ohne exakten Treffer das neutrale Rueckfallbild: besser ein Produktfoto ohne
      // Groessenangabe als eines mit der FALSCHEN Groesse.
      languageMediaFor(rawMediaId) {
        const map = this.variantMediaMap;
        if (map && map[String(rawMediaId)]) return map[String(rawMediaId)];
        return this.variantMediaFallback;
      }

      // Wie languageMediaFor, aber fuer die im Markup verwendete Form "<sectionId>-<mediaId>".
      resolveLanguageMediaId(prefixedMediaId) {
        const id = String(prefixedMediaId);
        const parts = id.match(/^(.*)-(\d+)$/);
        if (!parts) return null;
        const target = this.languageMediaFor(parts[2]);
        return target ? `${parts[1]}-${target}` : null;
      }

      setActiveMedia(mediaId, prepend) {
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        // HAPPY FARMS: Sprachbilder-Feature. Zeigt eine Sprache ihr eigenes Bild-Set,
        // existiert der Slide des Standard-Varianten-Bilds hier nicht. Dann wird auf das
        // passende Sprachbild umgeschluesselt; ab hier gilt durchgaengig die aufgeloeste ID
        // (sonst liefen Thumbnail- und Live-Region-Lookups weiter ins Leere).
        if (!activeMedia) {
          const resolvedMediaId = this.resolveLanguageMediaId(mediaId);
          if (resolvedMediaId) {
            const resolvedMedia = this.elements.viewer.querySelector(`[data-media-id="${resolvedMediaId}"]`);
            if (resolvedMedia) {
              activeMedia = resolvedMedia;
              mediaId = resolvedMediaId;
            }
          }
        }
        // Gibt es kein passendes Bild, bleibt das aktuelle stehen. Ohne diesen Guard wirft
        // classList.add auf null und bricht das komplette Varianten-Update (inkl. Preis) ab.
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
